import { useState, useRef, useCallback } from 'react';
import {
  Search, Send, Loader2, CheckCircle2, XCircle, Clock,
  ChevronDown, ChevronRight, Database, Code2, Shield,
  BarChart3, Brain, FileSearch, Cpu, Layers, Filter,
  ClipboardCheck, Activity, BookOpen, AlertTriangle,
  Copy, Check,
} from 'lucide-react';
import ingestionService from '../api';

// ---------------------------------------------------------------------------
// Agent pipeline definition
// ---------------------------------------------------------------------------
const AGENTS = [
  {
    step: 1, id: 'supervisor', name: 'Supervisor', criticality: 'Critical',
    description: 'Classifies intent and generates Query Plan IR',
    icon: Brain,
    getOutput: (r) => r?.query_plan ? (
      <div className="space-y-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-gray-500 font-semibold">Intent:</span>
          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] capitalize">{r.query_plan.intent || 'lookup'}</span>
          {r.query_plan.mode && <span className="px-1.5 py-0.5 border border-gray-300 rounded text-[10px]">{r.query_plan.mode}</span>}
        </div>
        {r.query_plan.metric && (
          <div><span className="text-gray-500 font-semibold">Metric: </span>
            <span className="font-mono bg-gray-50 px-2 py-0.5 rounded">{String(r.query_plan.metric)}</span>
          </div>
        )}
        {r.query_plan.filters?.length > 0 && (
          <div>
            <span className="text-gray-500 font-semibold">Filters:</span>
            <div className="space-y-1 mt-1">
              {r.query_plan.filters.map((f, i) => (
                <div key={i} className="font-mono bg-blue-50 px-2 py-1 rounded text-[11px]">
                  {f.column} {f.operator} {Array.isArray(f.value) ? `[${f.value.join(', ')}]` : f.value}
                </div>
              ))}
            </div>
          </div>
        )}
        {r.query_plan.confidence_target && (
          <div className="text-gray-600">
            <span className="text-gray-500 font-semibold">Confidence target: </span>
            {Math.round(r.query_plan.confidence_target * 100)}%
          </div>
        )}
      </div>
    ) : <span className="text-xs text-gray-400">Query plan generated</span>,
  },
  {
    step: 2, id: 'domain_router', name: 'Domain Router', criticality: 'Critical',
    description: 'Routes to relevant data products and tables',
    icon: FileSearch,
    getOutput: () => <span className="text-xs text-gray-600">Ranked candidate tables by semantic relevance</span>,
  },
  {
    step: 3, id: 'profiling_ranker', name: 'Profiling Ranker', criticality: 'High',
    description: 'Scores tables by certification, freshness and data quality',
    icon: BarChart3,
    getOutput: () => <span className="text-xs text-gray-600">Selected best-quality certified tables</span>,
  },
  {
    step: 4, id: 'schema_resolver', name: 'Schema Resolver', criticality: 'Critical',
    description: 'Builds JoinGraph with join paths from the knowledge graph',
    icon: Database,
    getOutput: () => <span className="text-xs text-gray-600">JoinGraph with table aliases and join edges constructed</span>,
  },
  {
    step: 5, id: 'decomposer', name: 'Decomposer', criticality: 'Medium',
    description: 'Breaks complex queries into SubQueryDAG',
    icon: Layers,
    getOutput: () => <span className="text-xs text-gray-600">Query complexity analysed — routed to composer</span>,
  },
  {
    step: 6, id: 'sql_composer', name: 'SQL Composer', criticality: 'Critical',
    description: 'LLM generates SQL from schema context and question',
    icon: Code2,
    getOutput: (r) => r?.sql?.length > 0 ? (
      <pre className="text-xs bg-gray-900 text-green-400 rounded p-2 overflow-x-auto max-h-24 leading-relaxed">{r.sql[0]}</pre>
    ) : <span className="text-xs text-gray-400">SQL generated</span>,
  },
  {
    step: 7, id: 'policy_enforcer', name: 'Policy Enforcer', criticality: 'Critical',
    description: 'Applies column masking, row security and RBAC',
    icon: Shield,
    getOutput: (r) => (
      <div className="text-xs">
        {r?.warnings?.some(w => w.toLowerCase().includes('mask'))
          ? <span className="text-yellow-700">Column masking applied</span>
          : <span className="text-green-700">Access approved — no restrictions triggered</span>}
      </div>
    ),
  },
  {
    step: 8, id: 'query_validator', name: 'Query Validator', criticality: 'Critical',
    description: 'Validates SQL syntax and LLM intent alignment',
    icon: ClipboardCheck,
    getOutput: (r) => {
      const retryCount = r?.evidence?.validator_retry_count || 0;
      const correctionHints = r?.evidence?.correction_hints || '';
      const validationWarnings = r?.evidence?.validation_warnings || [];
      return (
        <div className="text-xs space-y-2">
          {retryCount > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2 space-y-1">
              <div className="flex items-center gap-1.5 font-semibold text-yellow-800">
                <Activity className="h-3.5 w-3.5" />
                SQL Recomposed {retryCount} time{retryCount > 1 ? 's' : ''}
              </div>
              {correctionHints && (
                <div className="text-yellow-700 pl-5 text-[11px]">Validator feedback: {correctionHints}</div>
              )}
            </div>
          )}
          {Array.isArray(validationWarnings) && validationWarnings.length > 0
            ? validationWarnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1 text-yellow-700">
                  <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />{w}
                </div>
              ))
            : <span className="text-green-700 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> SQL validated — intent aligned</span>}
        </div>
      );
    },
  },
  {
    step: 9, id: 'executor', name: 'Executor', criticality: 'Critical',
    description: 'Executes SQL via pyodbc with connection pooling',
    icon: Cpu,
    getOutput: (r) => (
      <div className="text-xs space-y-1">
        <div><span className="text-gray-500">Rows returned:</span> <span className="font-semibold">{r?.answer?.row_count ?? 0}</span></div>
        <div><span className="text-gray-500">Execution time:</span> <span className="font-semibold">{r?.execution_time_ms}ms</span></div>
        <div><span className="text-gray-500">Status:</span>{' '}
          <span className={r?.execution_success ? 'text-green-700 font-semibold' : 'text-red-600 font-semibold'}>
            {r?.execution_success ? 'Success' : 'Failed'}
          </span>
        </div>
      </div>
    ),
  },
  {
    step: 10, id: 'interpreter', name: 'Interpreter', criticality: 'Medium',
    description: 'Converts results to natural language insights',
    icon: BookOpen,
    getOutput: (r) => {
      const insights = r?.evidence?.insights || [];
      const anomalies = r?.evidence?.anomalies || [];
      return (
        <div className="text-xs space-y-2">
          {r?.answer?.summary && <p className="text-gray-700 italic">&quot;{r.answer.summary}&quot;</p>}
          {Array.isArray(insights) && insights.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-primary">Key Insights</div>
              {insights.map((insight, i) => (
                <div key={i} className="flex items-start gap-1.5 text-gray-700 pl-2">
                  <span className="text-primary mt-0.5">•</span><span>{insight}</span>
                </div>
              ))}
            </div>
          )}
          {Array.isArray(anomalies) && anomalies.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-red-600">Anomalies</div>
              {anomalies.map((anomaly, i) => (
                <div key={i} className="flex items-start gap-1.5 bg-red-50 border border-red-200 rounded p-1.5">
                  <AlertTriangle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-red-700">{anomaly}</span>
                </div>
              ))}
            </div>
          )}
          {!r?.answer?.summary && !insights.length && !anomalies.length && (
            <span className="text-gray-400">Natural language interpretation generated</span>
          )}
        </div>
      );
    },
  },
  {
    step: 11, id: 'confidence_scorer', name: 'Confidence Scorer', criticality: 'Low',
    description: 'Aggregates pipeline confidence signals',
    icon: Activity,
    getOutput: (r) => {
      const pct = Math.round((r?.confidence?.overall_score ?? 0) * 100);
      return (
        <div className="text-xs space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Overall:</span>
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="font-semibold w-8 text-right">{pct}%</span>
          </div>
        </div>
      );
    },
  },
  {
    step: 12, id: 'audit_ledger', name: 'Audit Ledger', criticality: 'Low',
    description: 'Writes immutable audit record for compliance',
    icon: Filter,
    getOutput: (r) => (
      <div className="text-xs space-y-1">
        <div><span className="text-gray-500">Run ID:</span> <span className="font-mono text-primary">{r?.run_id}</span></div>
        <span className="text-green-700">Audit record persisted</span>
      </div>
    ),
  },
];

const AGENT_BY_ID = Object.fromEntries(AGENTS.map((a, i) => [a.id, i]));

const CRITICALITY_CLASS = {
  Critical: 'bg-red-50 text-red-700 border-red-200',
  High:     'bg-orange-50 text-orange-700 border-orange-200',
  Medium:   'bg-yellow-50 text-yellow-700 border-yellow-200',
  Low:      'bg-gray-50 text-gray-500 border-gray-200',
};

const CONNECTOR_TO_DIALECT = {
  sqlserver: 'mssql', mssql: 'mssql',
  postgres: 'postgresql', postgresql: 'postgresql',
  mysql: 'mysql', oracle: 'oracle',
};

// ---------------------------------------------------------------------------
// AgentCard
// ---------------------------------------------------------------------------
function AgentCard({ agent, state, response, expanded, onToggle }) {
  const Icon = agent.icon;

  const statusIcon = () => {
    if (state.status === 'running')   return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
    if (state.status === 'completed') return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    if (state.status === 'failed')    return <XCircle className="h-4 w-4 text-red-500" />;
    return <Clock className="h-4 w-4 text-gray-300" />;
  };

  const cardBorder = {
    idle:      'border-gray-200 bg-white',
    running:   'border-primary bg-blue-50 shadow-md ring-2 ring-blue-200',
    completed: 'border-green-300 bg-green-50',
    failed:    'border-red-300 bg-red-50',
    skipped:   'border-gray-200 bg-gray-50 opacity-50',
  }[state.status];

  const stepColor = {
    completed: 'bg-green-100 text-green-700',
    running:   'bg-blue-100 text-primaryDark',
    failed:    'bg-red-100 text-red-700',
  }[state.status] || 'bg-gray-100 text-gray-400';

  const iconColor = {
    completed: 'bg-green-100',
    running:   'bg-blue-100',
  }[state.status] || 'bg-gray-100';

  const iconFill = {
    completed: 'text-green-600',
    running:   'text-primary',
  }[state.status] || 'text-gray-400';

  return (
    <div className={`rounded-lg border-2 transition-all duration-300 ${cardBorder}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 text-left">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${stepColor}`}>
          {agent.step}
        </div>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${iconColor}`}>
          <Icon className={`h-4 w-4 ${iconFill}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 justify-between">
            <span className={`text-sm font-semibold truncate ${state.status === 'idle' ? 'text-gray-400' : 'text-gray-800'}`}>
              {agent.name}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${CRITICALITY_CLASS[agent.criticality]}`}>
                {agent.criticality}
              </span>
              {statusIcon()}
              {state.status === 'completed' && (
                expanded
                  ? <ChevronDown className="h-3 w-3 text-gray-400" />
                  : <ChevronRight className="h-3 w-3 text-gray-400" />
              )}
            </div>
          </div>
          <p className="text-xs text-gray-400 truncate">{agent.description}</p>
        </div>
      </button>

      {expanded && state.status === 'completed' && response && (
        <div className="px-3 pb-3 pt-0 border-t border-green-200">
          <div className="pt-2">{agent.getOutput(response)}</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResultsTable
// ---------------------------------------------------------------------------
function ResultsTable({ response }) {
  const rows = response.answer?.rows ?? [];
  const cols = response.answer?.columns ?? [];
  const [copied, setCopied] = useState(false);

  const copySQL = () => {
    navigator.clipboard.writeText(response.sql?.[0] ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!response.execution_success) {
    return (
      <div className="mt-6 rounded-lg bg-red-50 border border-red-200 p-4 flex items-start gap-3">
        <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold text-red-800 text-sm">Execution failed</p>
          <p className="text-red-600 text-xs mt-1">{response.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {response.sql?.[0] && (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-900">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Generated SQL</span>
            <button onClick={copySQL} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors">
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <pre className="text-sm bg-gray-950 text-green-400 px-4 py-3 overflow-x-auto leading-relaxed">{response.sql[0]}</pre>
        </div>
      )}

      {response.answer?.summary && (
        <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3">
          <p className="text-sm text-primaryDark italic">{response.answer.summary}</p>
        </div>
      )}

      {rows.length > 0 && cols.length > 0 && (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-xs font-medium text-gray-600">
              {response.answer?.row_count ?? rows.length} row{(response.answer?.row_count ?? rows.length) !== 1 ? 's' : ''} returned
            </span>
            <span className="text-xs text-gray-400">{response.execution_time_ms}ms</span>
          </div>
          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {cols.map(col => (
                    <th key={col} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {(Array.isArray(row) ? row : Object.values(row)).map((cell, ci) => (
                      <td key={ci} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-xs truncate">
                        {cell === null || cell === undefined ? <span className="text-gray-300">null</span> : String(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {response.confidence?.overall_score !== undefined && (
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span>Pipeline confidence:</span>
          <div className="flex-1 max-w-xs bg-gray-200 rounded-full h-1.5">
            <div
              className="h-1.5 rounded-full bg-primary transition-all"
              style={{ width: `${Math.round((response.confidence.overall_score ?? 0) * 100)}%` }}
            />
          </div>
          <span className="font-semibold text-gray-700">{Math.round((response.confidence.overall_score ?? 0) * 100)}%</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Example questions
// ---------------------------------------------------------------------------
const EXAMPLES = {
  Simple: [
    'List 10 customers with their account balances',
    'Show all accounts opened in the last 6 months',
    'Find advisors managing more than 50 clients',
  ],
  Medium: [
    'Show customers with total networth over 1M',
    'Find top 5 advisors by total client assets under management',
    'List accounts with balances declining more than 10% in the last quarter',
    'Which customer segments have the highest average account values?',
  ],
  Complex: [
    'Find married couples where both spouses are over 50 years old, have combined account values exceeding 500K, and share the same primary advisor',
    'Show month-over-month growth rate of new accounts by customer segment and advisor region',
    'Identify customers with multiple trust accounts whose total portfolio value increased by more than 20% YTD',
    'Calculate the average time-to-closure for high-value accounts (>100K) grouped by advisor experience level',
  ],
  Semantic: [
    'Show me all dormant accounts with their last activity date',
    'List primary account holders with conservative risk profiles',
    'Find customers flagged as high-risk in the last quarter',
    'Which joint accounts have both account holders marked as beneficiaries?',
    'Show active trust accounts where the trustee relationship is pending approval',
  ],
};

const EXAMPLE_COLORS = {
  Simple:   { btn: 'bg-green-50 text-green-700 hover:bg-green-100 border-green-200', dot: 'bg-green-400' },
  Medium:   { btn: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-200', dot: 'bg-yellow-400' },
  Complex:  { btn: 'bg-red-50 text-red-700 hover:bg-red-100 border-red-200', dot: 'bg-red-400' },
  Semantic: { btn: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200', dot: 'bg-blue-400' },
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const QueryPanel = ({ sourceId, sources = [] }) => {
  const [question, setQuestion] = useState('');
  const [selectedSourceId, setSelectedSourceId] = useState(sourceId ?? '');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [agentStates, setAgentStates] = useState(AGENTS.map(() => ({ status: 'idle' })));
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [expandedExamples, setExpandedExamples] = useState({});
  const abortRef = useRef(null);
  const inputRef = useRef(null);

  const hasRun = agentStates.some(s => s.status !== 'idle');

  const resolvedDialect = CONNECTOR_TO_DIALECT[
    (sources.find(s => s.id === selectedSourceId)?.connector ?? '').toLowerCase()
  ] ?? 'mssql';

  const runQuery = useCallback(async () => {
    if (!question.trim() || loading) return;
    setError(null);
    setResponse(null);
    setLoading(true);
    setAgentStates(AGENTS.map((_, i) => ({ status: i === 0 ? 'running' : 'idle' })));
    setExpandedAgent(null);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const selectedSource = sources.find(s => s.id === selectedSourceId);
    const userContext = { sql_dialect: resolvedDialect };
    if (selectedSourceId) userContext.source_id = selectedSourceId;
    if (selectedSource?.config) {
      userContext.source_config = { connector: selectedSource.connector, config: selectedSource.config };
    }

    const params = {
      question: question.trim(),
      user_context: userContext,
      mode: 'full_run',
      confidence_threshold: 0.5,
    };

    try {
      await ingestionService.textToSQLStream(
        params,
        (event) => {
          if (event.type === 'agent_done' && event.node) {
            const idx = AGENT_BY_ID[event.node] ?? -1;
            setAgentStates(prev => {
              const next = [...prev];
              if (idx >= 0) next[idx] = { status: 'completed' };
              const nextIdle = next.findIndex((s, i) => i > idx && s.status === 'idle');
              if (nextIdle >= 0) next[nextIdle] = { status: 'running' };
              return next;
            });
          } else if (event.type === 'complete') {
            if (event.result) setResponse(event.result);
            setAgentStates(AGENTS.map(() => ({ status: 'completed' })));
            setExpandedAgent(5);
            setLoading(false);
          } else if (event.type === 'error') {
            setError(event.message || 'Query failed');
            setAgentStates(prev => {
              const next = [...prev];
              const failAt = next.findIndex(s => s.status === 'running');
              if (failAt >= 0) {
                next[failAt] = { status: 'failed' };
                for (let i = failAt + 1; i < next.length; i++) next[i] = { status: 'skipped' };
              }
              return next;
            });
            setLoading(false);
          }
        },
        controller.signal,
      );
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setError(err instanceof Error ? err.message : 'Query failed');
        setAgentStates(prev => {
          const next = [...prev];
          const failAt = next.findIndex(s => s.status === 'running');
          if (failAt >= 0) {
            next[failAt] = { status: 'failed' };
            for (let i = failAt + 1; i < next.length; i++) next[i] = { status: 'skipped' };
          }
          return next;
        });
        setLoading(false);
      }
    }
  }, [question, loading, sources, selectedSourceId, resolvedDialect]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      runQuery();
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Scrollable content area */}
      <div className="space-y-6">
        {/* Header — only before first run */}
        {!hasRun && (
          <div className="text-center pt-4 pb-2">
            <p className="text-sm text-gray-500">
              12 AI agents collaborate in real-time — including clarification, decomposition, and semantic resolution
            </p>
          </div>
        )}

        {/* Example questions — only before first run */}
        {!hasRun && (
          <div className="space-y-2">
            {Object.entries(EXAMPLES).map(([group, queries]) => {
              const { btn, dot } = EXAMPLE_COLORS[group];
              const isOpen = !!expandedExamples[group];
              return (
                <div key={group}>
                  <button
                    onClick={() => setExpandedExamples(prev => ({ ...prev, [group]: !prev[group] }))}
                    className="w-full text-left flex items-center pl-4 gap-1.5 py-1.5 group"
                  >
                    <div className={`w-2 h-2 rounded-full ${dot} shrink-0`} />
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 group-hover:text-gray-700 transition-colors">{group}</span>
                    {isOpen
                      ? <ChevronDown className="w-3 h-3 text-gray-400" />
                      : <ChevronRight className="w-3 h-3 text-gray-400" />
                    }
                  </button>
                  {isOpen && (
                    <div className="flex flex-wrap gap-2 pl-5 mt-1 mb-2">
                      {queries.map(q => (
                        <button
                          key={q}
                          onClick={() => { setQuestion(q); inputRef.current?.focus(); }}
                          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors text-left ${btn}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Pipeline error</p>
              <p className="text-xs text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Agent pipeline visualization */}
        {hasRun && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Agent Pipeline</h3>
              {loading && (
                <span className="text-xs text-primary flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Processing through {AGENTS.length} agents...
                </span>
              )}
              {!loading && response && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> All agents completed in {response.execution_time_ms}ms
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {AGENTS.map((agent, idx) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  state={agentStates[idx]}
                  response={response}
                  expanded={expandedAgent === idx}
                  onToggle={() => setExpandedAgent(expandedAgent === idx ? null : idx)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {response && !loading && <ResultsTable response={response} />}
      </div>

      {/* Input bar — pinned to bottom with separator */}
      <div className="border-t border-gray-200 pt-4 flex gap-2">
        <select
          value={selectedSourceId}
          onChange={e => setSelectedSourceId(e.target.value)}
          disabled={loading}
          className="h-11 rounded-lg border border-gray-300 bg-white pl-3 pr-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary flex-shrink-0"
        >
          <option value="">Any source</option>
          {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about your data..."
            disabled={loading}
            className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50"
          />
        </div>
        <button
          onClick={runQuery}
          disabled={!question.trim() || loading}
          className="h-11 px-5 bg-primary hover:bg-primaryDark text-white font-medium rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {loading ? 'Running...' : 'Run'}
        </button>
      </div>
    </div>
  );
};

export default QueryPanel;
